import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { ArrowLeft, Loader2, Tag } from 'lucide-react';
import { toast } from 'sonner';

const NewClientPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    billing_address: '',
    service_address: '',
    notes: '',
    access_instructions: '',
    pets: '',
    preferred_days: [],
    preferred_times: '',
    status: 'active',
    category_id: '',
  });

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api().get('/admin/client-categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter(d => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a client name');
      return;
    }
    
    if (!formData.service_address.trim()) {
      toast.error('Please enter a service address');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api().post('/clients', {
        ...formData,
        preferred_days: formData.preferred_days.length > 0 ? formData.preferred_days : null,
        category_id: formData.category_id || null,
      });
      toast.success('Client created successfully!');
      navigate(`/clients/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/clients')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">New Client</h1>
          <p className="text-zinc-500 text-sm">Add a new client to your database</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Client name"
                className="mt-2"
                data-testid="client-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0400 000 000"
                  className="mt-2"
                  data-testid="client-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="client@example.com"
                  className="mt-2"
                  data-testid="client-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="mt-2" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="prospect">Prospect</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            {categories.length > 0 && (
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="mt-2" data-testid="category-select">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-zinc-400" />
                      <SelectValue placeholder="Select a category" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Addresses */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Addresses</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="service_address">Service Address *</Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={formData.service_address}
                  onChange={(address) => setFormData({ ...formData, service_address: address })}
                  placeholder="Start typing to search addresses..."
                  data-testid="service-address-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Search for an address or type it manually
              </p>
            </div>
            <div>
              <Label htmlFor="billing_address">Billing Address</Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={formData.billing_address}
                  onChange={(address) => setFormData({ ...formData, billing_address: address })}
                  placeholder="Leave blank if same as service address"
                  data-testid="billing-address-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Preferences & Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="access_instructions">Access Instructions</Label>
              <Textarea
                id="access_instructions"
                value={formData.access_instructions}
                onChange={(e) => setFormData({ ...formData, access_instructions: e.target.value })}
                placeholder="How to access the property (keys, codes, etc.)"
                className="mt-2"
                data-testid="access-instructions-input"
              />
            </div>

            <div>
              <Label htmlFor="pets">Pets</Label>
              <Input
                id="pets"
                value={formData.pets}
                onChange={(e) => setFormData({ ...formData, pets: e.target.value })}
                placeholder="e.g., 2 dogs, 1 cat"
                className="mt-2"
                data-testid="pets-input"
              />
            </div>

            <div>
              <Label>Preferred Days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {weekDays.map((day) => (
                  <Button
                    key={day}
                    type="button"
                    variant={formData.preferred_days.includes(day) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleDayToggle(day)}
                    className={formData.preferred_days.includes(day) ? 'bg-zinc-900' : ''}
                    data-testid={`day-${day.toLowerCase()}`}
                  >
                    {day.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="preferred_times">Preferred Times</Label>
              <Input
                id="preferred_times"
                value={formData.preferred_times}
                onChange={(e) => setFormData({ ...formData, preferred_times: e.target.value })}
                placeholder="e.g., Morning, After 2pm"
                className="mt-2"
                data-testid="preferred-times-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">General Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any other important information about this client"
                className="mt-2"
                data-testid="notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/clients')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-zinc-900 hover:bg-zinc-800"
            disabled={loading}
            data-testid="create-client-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Client'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewClientPage;
